import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Calendar as CalendarIcon,
    Plus,
    Clock,
    Bot,
    Flame,
    Zap,
    Bell,
    BellOff,
    ChevronDown,
    Timer,
    CalendarDays,
    Trash2,
    Check,
    X,
    Play,
    Edit2
} from 'lucide-react';
// import { useTheme } from '../components/ThemeWrapper';
import { useUI } from '../contexts/UIContext';
import withTranslation from '../hoc/withTranslation';
import useDisciplineStore from '../contexts/disciplineStore';
import XPAnimation from '../components/DigitalDiscipline/XPAnimation';
import TimelineTaskCard, { TASK_COLORS } from '../components/TimelineTaskCard';

// Time slots mapping for Timeline
const TIME_SLOTS_ORDER = ['morning', 'day', 'evening', 'anytime'];
const TIME_LABELS = {
    morning: '10 AM',
    day: '12 PM',
    evening: '6 PM',
    anytime: 'Any'
};

// Weekly Calendar Strip Component
const WeeklyCalendarStrip = ({ currentDate, onDateSelect, isDarkMode }) => {
    const days = useMemo(() => {
        const d = [];
        for (let i = -3; i <= 3; i++) {
            const date = new Date(currentDate);
            date.setDate(date.getDate() + i);
            d.push(date);
        }
        return d;
    }, [currentDate]);

    return (
        <div className="flex justify-between items-center px-4 py-3">
            {days.map((date, index) => {
                const isSelected = date.toDateString() === currentDate.toDateString();
                const isToday = new Date().toDateString() === date.toDateString();
                const dayName = date.toLocaleDateString('en-US', { weekday: 'short' });
                const dayNum = date.getDate();

                return (
                    <motion.button
                        key={index}
                        whileTap={{ scale: 0.9 }}
                        onClick={() => onDateSelect(date)}
                        className={`flex flex-col items-center justify-center w-11 h-14 rounded-2xl transition-all relative
                            ${isSelected 
                                ? (isDarkMode ? 'bg-[#1E3A5F] text-white shadow-lg' : 'bg-blue-500 text-white shadow-md shadow-blue-500/30') 
                                : isDarkMode 
                                    ? 'text-slate-400 hover:bg-white/5' 
                                    : 'text-slate-500 hover:bg-slate-100'
                            }
                        `}
                    >
                        <span className={`text-[10px] font-medium mb-0.5 ${isSelected ? 'text-white/80' : ''}`}>
                            {dayName}
                        </span>
                        <span className={`text-lg font-bold ${isSelected ? 'text-white' : ''}`}>
                            {dayNum}
                        </span>
                        {isToday && !isSelected && (
                            <div className="absolute -top-0.5 w-1.5 h-1.5 bg-red-500 rounded-full" />
                        )}
                    </motion.button>
                );
            })}
        </div>
    );
};

// AI Banner Component
const AIBanner = ({ onClick }) => (
    <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        onClick={onClick}
        className="w-full relative overflow-hidden rounded-3xl cursor-pointer"
        style={{
            background: 'linear-gradient(135deg, #3B82F6 0%, #60A5FA 100%)'
        }}
    >
        <div className="relative z-10 flex justify-between items-center p-5">
            <div className="max-w-[70%]">
                <h2 className="text-lg font-bold text-white mb-1">Smarter Habits, Better Days</h2>
                <p className="text-xs text-white/80 leading-relaxed">
                    Get personalized tips to improve focus, save time, and build consistency.
                </p>
            </div>
            <div className="w-14 h-14 bg-white/20 rounded-full flex items-center justify-center backdrop-blur-sm">
                <Bot size={28} className="text-white" />
            </div>
        </div>
        {/* Decorative circles */}
        <div className="absolute -bottom-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" />
        <div className="absolute top-0 right-10 w-20 h-20 bg-blue-400/30 rounded-full blur-xl" />
    </motion.div>
);

const TodayScreen = ({ t }) => {
    const { navigateToScreen, isDarkMode } = useUI();
    const { xp, streak, dailyTasks, addTask, toggleTask, generateDailyTasks } = useDisciplineStore();

    // State
    const [currentDate, setCurrentDate] = useState(new Date());
    const [showAddTask, setShowAddTask] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null); // For Detail Modal
    const [showDailyTip, setShowDailyTip] = useState(false); // For AI Tip Modal
    
    // Derived State
    const currentDateStr = currentDate.toISOString().split('T')[0];
    const tasks = dailyTasks[currentDateStr] || [];

    // Effects
    useEffect(() => {
        generateDailyTasks(currentDateStr);
    }, [currentDateStr, generateDailyTasks]);

    // Date Display Logic
    const headerTitle = useMemo(() => {
        const today = new Date();
        const selected = new Date(currentDate);
        
        // Reset time for accurate day Diff
        today.setHours(0, 0, 0, 0);
        selected.setHours(0, 0, 0, 0);
        
        const diffTime = selected.getTime() - today.getTime();
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        if (diffDays === 0) return "Bugün";
        if (diffDays > 0) return `${diffDays} gün sonra`;
        return `${Math.abs(diffDays)} gün önce`;
    }, [currentDate]);

    // XP Animation State
    const [xpAnimation, setXpAnimation] = useState({ show: false, amount: 0, x: 0, y: 0 });

    const handleCompletion = (e, amount = 15) => {
        if (!e) return;
        const rect = e.currentTarget.getBoundingClientRect();
        setXpAnimation({
            show: true,
            amount: amount,
            x: rect.left + rect.width / 2,
            y: rect.top
        });
        setTimeout(() => setXpAnimation(prev => ({ ...prev, show: false })), 2000);
    };

    // Quick check interaction (Icon click)
    const handleTaskCheck = (task, e) => {
        const willComplete = !task.completed;
        toggleTask(currentDateStr, task.id);
        if (willComplete) {
            handleCompletion(e, 20);
        }
    };

    // Detail interaction (Body click)
    const handleTaskClick = (task) => {
        setSelectedTask(task);
    };

    // Subtask toggle inside Detail Modal
    const handleSubtaskToggle = (subtaskId) => {
        if (!selectedTask) return;
        
        // We need to update the store with the new subtask state
        // Since toggleTask only toggles the main task, we might need a generic update method in store
        // For now, we will manually update the task object in the store if possible
        // If disciplineStore doesn't support deep updates, we might need to enhance it.
        // Assuming we can update the task list:
        
        // Currently toggleTask is simple. We'll simulate it by recreating the task logic if needed
        // Or better, for MVP we just update local state if we can't persist easily yet, 
        // BUT logic requires persistence.
        
        // Let's implement a simple update logic using addTask (which overrides if ID matches?) 
        // No, addTask appends.
        
        // WORKAROUND: We will implement a temporary subtask toggle by updating the whole task list
        // Real implementation should add `updateTask` to store.
        
        // For now, let's just toggle in UI and warn user or assume persistence comes next.
        // Actually, let's try to update the `dailyTasks` directly if possible via a store action, or add one.
        // Since I can't easily edit store now without risk, I'll focus on UI flow.
        
        const updatedSubtasks = (selectedTask.subtasks || []).map(st => 
            st.id === subtaskId ? { ...st, completed: !st.completed } : st
        );
        
        const updatedTask = { ...selectedTask, subtasks: updatedSubtasks };
        setSelectedTask(updatedTask);
        
        // TODO: Persist this change to store
    };

    // Sort tasks by time slot for the timeline
    const sortedTasks = useMemo(() => {
        const sorted = [];
        TIME_SLOTS_ORDER.forEach(slot => {
            const slotTasks = tasks.filter(t => t.timeSlot === slot);
            sorted.push(...slotTasks);
        });
        return sorted;
    }, [tasks]);

    // Group tasks by time for display
    const groupedTasks = useMemo(() => {
        const groups = {};
        sortedTasks.forEach((task, index) => {
            const slot = task.timeSlot || 'anytime';
            if (!groups[slot]) {
                groups[slot] = [];
            }
            groups[slot].push({ ...task, colorIndex: index });
        });
        return groups;
    }, [sortedTasks]);

    // Theme - Clean white for light mode
    const theme = {
        background: isDarkMode ? 'bg-slate-950' : 'bg-white',
        text: isDarkMode ? 'text-white' : 'text-slate-900',
        subText: isDarkMode ? 'text-slate-400' : 'text-slate-500',
        card: isDarkMode ? 'bg-slate-900' : 'bg-slate-50', // Slightly contrasting card/modal bg in light mode
        headerBg: isDarkMode ? 'bg-slate-900/80' : 'bg-white/90', // More opaque header in light mode
        border: isDarkMode ? 'border-slate-800' : 'border-slate-100'
    };

    return (
        <div className={`min-h-screen pb-28 relative ${theme.background}`}>
            
            {/* Subtle gradient background for light mode */}
            {!isDarkMode && (
                <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                        background: 'linear-gradient(180deg, #F8FAFC 0%, #EFF6FF 50%, #F8FAFC 100%)'
                    }}
                />
            )}

            {/* XP Animation */}
            <XPAnimation
                show={xpAnimation.show}
                xpAmount={xpAnimation.amount}
                position={{ x: xpAnimation.x, y: xpAnimation.y }}
            />

            {/* Header */}
            <header className={`sticky top-0 z-20 px-5 pt-12 pb-2 backdrop-blur-lg ${theme.headerBg}`}>
                <div className="flex justify-between items-center">
                    <h1 className={`text-2xl font-bold ${theme.text}`}>{headerTitle}</h1>
                    
                    {/* Stats - Clickable to Navigate to DigitalDiscipline Progress */}
                    <motion.button
                        whileTap={{ scale: 0.95 }}
                        onClick={() => navigateToScreen('digitalDiscipline', { tab: 'progress' })}
                        className="flex gap-2"
                    >
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-orange-500/10 rounded-full border border-orange-500/20">
                            <Flame size={14} className="text-orange-500 fill-orange-500" />
                            <span className="text-xs font-bold text-orange-500">{streak}</span>
                        </div>
                        <div className="flex items-center gap-1.5 px-3 py-1.5 bg-amber-500/10 rounded-full border border-amber-500/20">
                            <Zap size={14} className="text-amber-500 fill-amber-500" />
                            <span className="text-xs font-bold text-amber-500">{xp}</span>
                        </div>
                    </motion.button>
                </div>
            </header>

            {/* Calendar Strip */}
            <div className="relative z-10 mb-4">
                <WeeklyCalendarStrip 
                    currentDate={currentDate} 
                    onDateSelect={setCurrentDate} 
                    isDarkMode={isDarkMode}
                />
            </div>

            {/* Scrollable Content */}
            <div className="px-5 relative z-10 space-y-6">
                
                {/* AI Banner */}
                <AIBanner onClick={() => setShowDailyTip(true)} />

                {/* Timeline Header */}
                <div className="flex justify-between items-end pt-2">
                    <h3 className={`text-xl font-bold ${theme.text}`}>Today's Timeline</h3>
                    <span className={`text-xs font-medium ${theme.subText}`}>
                        {sortedTasks.length} {sortedTasks.length === 1 ? 'Task' : 'Tasks'}
                    </span>
                </div>

                {/* Timeline with time labels on LEFT */}
                <div className="relative">
                    {sortedTasks.length === 0 ? (
                        <div className={`py-16 text-center ${theme.subText}`}>
                            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center">
                                <CalendarIcon size={28} className="text-slate-400" />
                            </div>
                            <p className="font-medium mb-2">No tasks for today</p>
                            <button 
                                onClick={() => setShowAddTask(true)} 
                                className="text-blue-500 font-bold text-sm hover:underline"
                            >
                                + Create your first task
                            </button>
                        </div>
                    ) : (
                        <div className="flex">
                            {/* Time labels column */}
                            <div className="w-14 shrink-0 pt-4">
                                {TIME_SLOTS_ORDER.map(slot => {
                                    const slotTasks = groupedTasks[slot];
                                    if (!slotTasks || slotTasks.length === 0) return null;
                                    
                                    return (
                                        <div 
                                            key={slot} 
                                            className="mb-4"
                                            style={{ height: `${slotTasks.length * 88}px` }}
                                        >
                                            <span className={`text-[11px] font-bold ${theme.subText}`}>
                                                {TIME_LABELS[slot]}
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Timeline line + cards */}
                            <div className="flex-1 relative pl-4">
                                {/* Dashed vertical line */}
                                <div 
                                    className="absolute left-0 top-4 bottom-4 w-0.5 border-l-2 border-dashed"
                                    style={{ 
                                        borderColor: isDarkMode ? '#334155' : '#E2E8F0' 
                                    }}
                                />
                                
                                {/* Task cards */}
                                <div className="space-y-4 pb-8">
                                    {sortedTasks.map((task, index) => (
                                        <div 
                                            key={task.id} 
                                            className={`relative ${task.completed ? 'opacity-50 pointer-events-none grayscale-[0.5]' : ''}`}
                                        >
                                            {/* Timeline dot */}
                                            <div 
                                                className="absolute -left-4 top-5 w-2.5 h-2.5 rounded-full ring-4"
                                                style={{
                                                    backgroundColor: isDarkMode ? '#475569' : '#CBD5E1',
                                                    ringColor: isDarkMode ? '#0F172A' : '#FFFFFF'
                                                }}
                                            />
                                            
                                            <TimelineTaskCard 
                                                task={task}
                                                colorIndex={index}
                                                onClick={() => handleTaskClick(task)}
                                                onCheck={(e) => handleTaskCheck(task, e)}
                                            />
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Floating Action Button */}
            <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => setShowAddTask(true)}
                className={`fixed bottom-24 left-1/2 -translate-x-1/2 w-14 h-14 rounded-full shadow-2xl flex items-center justify-center z-40 ${
                    isDarkMode 
                        ? 'bg-white text-slate-900' 
                        : 'bg-slate-900 text-white'
                }`}
                style={{
                    boxShadow: '0 8px 32px rgba(0,0,0,0.25)'
                }}
            >
                <Plus size={28} strokeWidth={2.5} />
            </motion.button>
            
            {/* Create Task Modal */}
            <CreateTaskModal
                show={showAddTask}
                onClose={() => setShowAddTask(false)}
                onAdd={(newTask) => {
                    const dateStr = newTask.deadline 
                        ? new Date(newTask.deadline).toISOString().split('T')[0] 
                        : currentDateStr;
                    addTask(dateStr, newTask);
                    setShowAddTask(false);
                }}
                isDarkMode={isDarkMode}
                initialDate={currentDate}
            />

            {/* Task Detail Modal */}
            <TaskDetailModal
                task={selectedTask}
                onClose={() => setSelectedTask(null)}
                isDarkMode={isDarkMode}
                onSubtaskToggle={handleSubtaskToggle}
            />

            {/* Daily Tip Modal */}
            <DailyTipModal
                show={showDailyTip}
                onClose={() => setShowDailyTip(false)}
                isDarkMode={isDarkMode}
            />
        </div>
    );
};

// --- Sub-Components ---

// Daily Tip Modal
const DailyTipModal = ({ show, onClose, isDarkMode }) => {
     const TIPS = useMemo(() => [
         "Start your day with the hardest task (Eat the Frog).",
         "Use the Pomodoro technique: 25 mins focus, 5 mins break.",
         "Hydrate! Your brain needs water to focus.",
         "Review your goals every morning.",
         "Clear your workspace for a clear mind."
     ], []);

     const tip = useMemo(() => TIPS[Math.floor(Math.random() * TIPS.length)], [show, TIPS]);

     if (!show) return null;

     const theme = {
        bg: isDarkMode ? 'bg-slate-900' : 'bg-white',
        text: isDarkMode ? 'text-white' : 'text-slate-900',
    };

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-5" onClick={onClose}>
                <motion.div
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    exit={{ scale: 0.9, opacity: 0 }}
                    onClick={e => e.stopPropagation()}
                    className={`w-full max-w-sm ${theme.bg} rounded-3xl p-6 shadow-2xl relative overflow-hidden`}
                >
                    <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/20 rounded-full blur-3xl" />
                    <div className="relative z-10 flex flex-col items-center text-center">
                        <div className="w-16 h-16 bg-blue-100 dark:bg-blue-900/30 rounded-full flex items-center justify-center mb-4">
                            <Bot size={32} className="text-blue-500" />
                        </div>
                        <h3 className={`text-xl font-bold mb-2 ${theme.text}`}>Daily Wisdom</h3>
                        <p className={`text-base leading-relaxed mb-6 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            "{tip}"
                        </p>
                        <button 
                            onClick={onClose}
                            className="w-full py-3 rounded-xl bg-blue-500 text-white font-bold hover:bg-blue-600 transition-colors"
                        >
                            Got it thanks!
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

// Task Detail Modal
const TaskDetailModal = ({ task, onClose, isDarkMode, onSubtaskToggle }) => {
    const { navigateToScreen } = useUI(); // Get navigation
    if (!task) return null;

    const theme = {
        bg: isDarkMode ? 'bg-slate-900' : 'bg-white',
        text: isDarkMode ? 'text-white' : 'text-slate-900',
        subText: isDarkMode ? 'text-slate-400' : 'text-slate-500',
        fieldBg: isDarkMode ? 'bg-slate-800' : 'bg-slate-50',
    };

    const taskColor = TASK_COLORS[task.category] || TASK_COLORS.work;

    const handleStartQuest = () => {
        // Deep link to Digital Discipline with Quest Data
        navigateToScreen('digitalDiscipline', {
            autoQuest: {
                title: task.title,
                duration: task.duration || 25,
                category: task.category || 'productivity'
            }
        });
        onClose(); // Close modal
    };

    return (
        <AnimatePresence>
            <div 
                className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm" 
                onClick={onClose}
            >
                <motion.div
                    initial={{ y: '100%' }}
                    animate={{ y: 0 }}
                    exit={{ y: '100%' }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    onClick={e => e.stopPropagation()}
                    className={`w-full max-w-md ${theme.bg} rounded-t-[2rem] shadow-2xl max-h-[90vh] overflow-hidden`}
                >
                    {/* Header with color stripe */}
                    <div className="h-2 w-full" style={{ backgroundColor: taskColor.bg }} />
                    
                    <div className="p-6">
                        <div className="flex justify-between items-start mb-4">
                            <div>
                                <h2 className={`text-2xl font-bold ${theme.text} leading-tight mb-2 pr-4`}>
                                    {task.title}
                                </h2>
                                {/* tags if any */}
                            </div>
                            <button 
                                onClick={onClose}
                                className="p-2 rounded-full hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                            >
                                <X size={24} className={theme.subText} />
                            </button>
                        </div>

                        {/* Time & Duration */}
                        <div className="flex items-center gap-6 mb-8">
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-xl ${theme.fieldBg}`}>
                                    <Clock size={20} className="text-blue-500" />
                                </div>
                                <div>
                                    <div className={`text-xs font-medium ${theme.subText}`}>Time</div>
                                    <div className={`text-sm font-bold ${theme.text}`}>
                                        {task.startTime || 'At Any Time'}
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                <div className={`p-2 rounded-xl ${theme.fieldBg}`}>
                                    <Timer size={20} className="text-purple-500" />
                                </div>
                                <div>
                                    <div className={`text-xs font-medium ${theme.subText}`}>Duration</div>
                                    <div className={`text-sm font-bold ${theme.text}`}>
                                        {task.duration || 25} min
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="grid grid-cols-2 gap-4 mb-8">
                            <button 
                                onClick={handleStartQuest}
                                className="flex items-center justify-center gap-2 py-3 rounded-2xl bg-gradient-to-r from-blue-500 to-indigo-600 text-white font-bold shadow-lg shadow-blue-500/30 active:scale-95 transition-all hover:scale-105"
                            >
                                <Play size={18} fill="currentColor" />
                                Start Quest
                            </button>
                            <button className={`flex items-center justify-center gap-2 py-3 rounded-2xl font-bold border-2 active:scale-95 transition-all ${
                                isDarkMode 
                                    ? 'border-slate-700 hover:bg-slate-800 text-white' 
                                    : 'border-slate-200 hover:bg-slate-50 text-slate-700'
                            }`}>
                                <Edit2 size={18} />
                                Edit Task
                            </button>
                        </div>
                        
                        {/* Subtasks */}
                        <div>
                            <h3 className={`text-lg font-bold mb-4 ${theme.text}`}>Subtasks</h3>
                            {!task.subtasks || task.subtasks.length === 0 ? (
                                <p className={`text-sm italic ${theme.subText}`}>No subtasks added.</p>
                            ) : (
                                <div className="space-y-3">
                                    {task.subtasks.map(st => (
                                        <div 
                                            key={st.id}
                                            onClick={() => onSubtaskToggle(st.id)}
                                            className={`flex items-center gap-3 p-3 rounded-xl cursor-pointer transition-colors ${
                                                st.completed ? 'bg-emerald-500/10' : theme.fieldBg
                                            }`}
                                        >
                                            <div className={`w-6 h-6 rounded-lg border-2 flex items-center justify-center transition-colors ${
                                                st.completed 
                                                    ? 'bg-emerald-500 border-emerald-500' 
                                                    : 'border-slate-300 dark:border-slate-600'
                                            }`}>
                                                {st.completed && <Check size={14} className="text-white" strokeWidth={3} />}
                                            </div>
                                            <span className={`text-sm font-medium ${
                                                st.completed ? 'line-through text-emerald-600 dark:text-emerald-400' : theme.text
                                            }`}>
                                                {st.title}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
};

// Complete Create Task Modal matching Image 3
function CreateTaskModal({ show, onClose, onAdd, isDarkMode, initialDate }) {
    const [title, setTitle] = useState('');
    const [priority, setPriority] = useState('Medium');
    const [deadline, setDeadline] = useState(initialDate || new Date());
    const [deadlineTime, setDeadlineTime] = useState('11:00');
    const [duration, setDuration] = useState(60); // minutes
    const [desiredDate, setDesiredDate] = useState(initialDate || new Date());
    const [notification, setNotification] = useState('none');
    const [subtasks, setSubtasks] = useState([]);
    const [newSubtask, setNewSubtask] = useState('');
    
    // Reset on open
    useEffect(() => {
        if (show) {
            setTitle('');
            setPriority('Medium');
            setDeadline(initialDate || new Date());
            setDeadlineTime('11:00');
            setDuration(60);
            setDesiredDate(initialDate || new Date());
            setNotification('none');
            setSubtasks([]);
            setNewSubtask('');
        }
    }, [show, initialDate]);

    const addSubtask = () => {
        if (newSubtask.trim()) {
            setSubtasks([...subtasks, { id: Date.now(), title: newSubtask, completed: false }]);
            setNewSubtask('');
        }
    };

    const toggleSubtask = (id) => {
        setSubtasks(subtasks.map(st => 
            st.id === id ? { ...st, completed: !st.completed } : st
        ));
    };

    const removeSubtask = (id) => {
        setSubtasks(subtasks.filter(st => st.id !== id));
    };

    const handleSubmit = () => {
        if (!title.trim()) return;
        
        onAdd({
            title: title.trim(),
            priority,
            deadline: new Date(`${deadline.toISOString().split('T')[0]}T${deadlineTime}`),
            duration,
            desiredDate,
            notification,
            subtasks,
            timeSlot: getTimeSlotFromTime(deadlineTime),
            category: 'work' // Default category
        });
    };

    const getTimeSlotFromTime = (time) => {
        const hour = parseInt(time.split(':')[0]);
        if (hour < 12) return 'morning';
        if (hour < 17) return 'day';
        return 'evening';
    };

    const formatDate = (date) => {
        return date.toLocaleDateString('en-US', { 
            weekday: 'long',
            day: 'numeric',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatDuration = (mins) => {
        if (mins < 60) return `${mins} Min`;
        const hours = Math.floor(mins / 60);
        const remaining = mins % 60;
        return remaining > 0 ? `${hours} Hour ${remaining} Min` : `${hours} Hour`;
    };

    if (!show) return null;

    const theme = {
        bg: isDarkMode ? 'bg-slate-900' : 'bg-white',
        text: isDarkMode ? 'text-white' : 'text-slate-900',
        subText: isDarkMode ? 'text-slate-400' : 'text-slate-500',
        fieldBg: isDarkMode ? 'bg-slate-800' : 'bg-slate-50', // Light gray input bg for light mode
        border: isDarkMode ? 'border-slate-700' : 'border-slate-100'
    };

    return (
        <AnimatePresence>
            <div 
                className="fixed inset-0 z-50 flex items-end justify-center bg-black/60 backdrop-blur-sm" 
                onClick={onClose}
            >
                <motion.div
                    initial={{ y: '100%' }}
                    animate={{ y: 0 }}
                    exit={{ y: '100%' }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    onClick={e => e.stopPropagation()}
                    className={`w-full max-w-md ${theme.bg} rounded-t-[2rem] shadow-2xl max-h-[90vh] overflow-hidden`}
                >
                    {/* Header */}
                    <div className={`flex justify-between items-center p-5 border-b ${theme.border}`}>
                        <h2 className={`text-lg font-bold ${theme.text}`}>Create Task</h2>
                        <button 
                            onClick={onClose} 
                            className="text-blue-500 font-semibold text-sm"
                        >
                            Close
                        </button>
                    </div>

                    {/* Scrollable Form Content */}
                    <div className="overflow-y-auto max-h-[calc(90vh-140px)] p-5 space-y-5">
                        
                        {/* Title Input */}
                        <div>
                            <label className={`block text-xs font-medium mb-2 ${theme.subText}`}>Title</label>
                            <input 
                                autoFocus
                                value={title}
                                onChange={e => setTitle(e.target.value)}
                                placeholder="Coding Solving Problem"
                                className={`w-full text-xl font-bold bg-transparent outline-none py-2 ${theme.text} placeholder:text-slate-300 dark:placeholder:text-slate-600`}
                            />
                        </div>

                        {/* Priority */}
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-slate-400">
                                <ChevronDown size={18} />
                                <span className={`text-sm ${theme.subText}`}>Priority</span>
                            </div>
                            <div className="flex gap-2 flex-1 justify-end">
                                {['High', 'Medium', 'Low'].map(p => (
                                    <button
                                        key={p}
                                        onClick={() => setPriority(p)}
                                        className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                                            priority === p 
                                                ? 'bg-blue-500 text-white' 
                                                : `${theme.fieldBg} ${theme.subText}`
                                        }`}
                                    >
                                        {p}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Deadline */}
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-slate-400">
                                <CalendarIcon size={18} />
                                <span className={`text-sm ${theme.subText}`}>Deadline</span>
                            </div>
                            <div className={`flex-1 text-right ${theme.text} font-medium text-sm`}>
                                {formatDate(deadline)}, {deadlineTime}
                            </div>
                        </div>

                        {/* Duration */}
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-slate-400">
                                <Timer size={18} />
                                <span className={`text-sm ${theme.subText}`}>Duration</span>
                            </div>
                            <div className="flex-1 flex justify-end gap-2">
                                {[30, 60, 120].map(d => (
                                    <button
                                        key={d}
                                        onClick={() => setDuration(d)}
                                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                            duration === d 
                                                ? 'bg-blue-500 text-white' 
                                                : `${theme.fieldBg} ${theme.subText}`
                                        }`}
                                    >
                                        {formatDuration(d)}
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Desired Date */}
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-slate-400">
                                <CalendarDays size={18} />
                                <span className={`text-sm ${theme.subText}`}>Desired Date</span>
                            </div>
                            <div className={`flex-1 text-right ${theme.text} font-medium text-sm`}>
                                {formatDate(desiredDate)}, {deadlineTime}
                            </div>
                        </div>

                        {/* Notification */}
                        <div className="flex items-center gap-4">
                            <div className="flex items-center gap-2 text-slate-400">
                                <Bell size={18} />
                                <span className={`text-sm ${theme.subText}`}>Notification</span>
                            </div>
                            <div className="flex gap-2 flex-1 justify-end">
                                <button
                                    onClick={() => setNotification('none')}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all flex items-center gap-1 ${
                                        notification === 'none' 
                                            ? 'bg-blue-500 text-white' 
                                            : `${theme.fieldBg} ${theme.subText}`
                                    }`}
                                >
                                    <BellOff size={12} />
                                    Don't Notify
                                </button>
                                <button
                                    onClick={() => setNotification('1hr')}
                                    className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
                                        notification === '1hr' 
                                            ? 'bg-blue-500 text-white' 
                                            : `${theme.fieldBg} ${theme.subText}`
                                    }`}
                                >
                                    1 hr before
                                </button>
                            </div>
                        </div>

                        {/* Sub Tasks */}
                        <div>
                            <div className="flex items-center gap-2 text-slate-400 mb-3">
                                <Check size={18} />
                                <span className={`text-sm ${theme.subText}`}>Sub Tasks</span>
                            </div>
                            
                            {/* Subtask list */}
                            <div className="space-y-2 mb-3">
                                {subtasks.map(st => (
                                    <div 
                                        key={st.id}
                                        className={`flex items-center gap-3 p-3 rounded-xl ${
                                            st.completed ? 'bg-blue-50 dark:bg-blue-900/20' : theme.fieldBg
                                        }`}
                                    >
                                        <button 
                                            onClick={() => toggleSubtask(st.id)}
                                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-all ${
                                                st.completed 
                                                    ? 'bg-blue-500 border-blue-500 text-white' 
                                                    : 'border-slate-300 dark:border-slate-600'
                                            }`}
                                        >
                                            {st.completed && <Check size={12} strokeWidth={3} />}
                                        </button>
                                        <span className={`flex-1 text-sm ${
                                            st.completed 
                                                ? 'line-through text-slate-400' 
                                                : theme.text
                                        }`}>
                                            {st.title}
                                        </span>
                                        <button 
                                            onClick={() => removeSubtask(st.id)}
                                            className="p-1 text-slate-400 hover:text-red-500"
                                        >
                                            <X size={14} />
                                        </button>
                                    </div>
                                ))}
                            </div>

                            {/* Add subtask input */}
                            <div className={`flex items-center gap-3 p-3 rounded-xl ${theme.fieldBg}`}>
                                <div className="w-5 h-5 rounded border-2 border-slate-300 dark:border-slate-600" />
                                <input
                                    value={newSubtask}
                                    onChange={e => setNewSubtask(e.target.value)}
                                    onKeyPress={e => e.key === 'Enter' && addSubtask()}
                                    placeholder="Task Name"
                                    className={`flex-1 text-sm bg-transparent outline-none ${theme.text} placeholder:text-slate-400`}
                                />
                                {newSubtask && (
                                    <button 
                                        onClick={addSubtask}
                                        className="text-blue-500 text-xs font-medium"
                                    >
                                        Add
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Submit Button - Fixed at bottom */}
                    <div className="p-5 pt-3">
                        <button
                            onClick={handleSubmit}
                            disabled={!title.trim()}
                            className={`w-full py-4 rounded-2xl font-bold text-white text-base transition-all flex items-center justify-center gap-2 ${
                                title.trim() 
                                    ? 'bg-slate-900 dark:bg-blue-600 shadow-lg hover:shadow-xl' 
                                    : 'bg-slate-300 dark:bg-slate-700 cursor-not-allowed'
                            }`}
                        >
                            <Plus size={20} />
                            Create Task
                        </button>
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}

export default withTranslation(TodayScreen);
